import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Phone, Calendar, MapPin, LogOut, Plus, LayoutDashboard, Settings, AlertTriangle, Bell, BarChart3, CalendarDays } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function BrokerDashboard() {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [todayLeads, setTodayLeads] = useState([]);
  const [recentLeads, setRecentLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    const token = localStorage.getItem('token');
    try {
      const [statsRes, leadsRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/leads?limit=1000`, { headers: { Authorization: `Bearer ${token}` } }) // Get more leads for calculations
      ]);
      
      // Backend now provides: backlog (date-based), site_visits_done counts
      // No need to calculate on frontend anymore
      setStats(statsRes.data);
      
      const today = new Date().toISOString().split('T')[0];
      const allLeads = leadsRes.data.leads || leadsRes.data; // Handle both paginated and non-paginated response
      
      const todayTasks = allLeads.filter(l => l.next_call_date === today);
      setTodayLeads(todayTasks);
      setRecentLeads(allLeads.slice(0, 10));
      
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'New': 'bg-blue-100 text-blue-700',
      'Booked': 'bg-green-100 text-green-700',
      'Lost': 'bg-red-100 text-red-700',
      'Site Visit Planned': 'bg-purple-100 text-purple-700',
      'Site Visit Done': 'bg-indigo-100 text-indigo-700',
      'Follow up': 'bg-amber-100 text-amber-700'
    };
    return colors[status] || 'bg-slate-100 text-slate-700';
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="broker-dashboard">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10 shadow-sm">
        <div className="px-4 py-3 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-slate-800" style={{ fontFamily: 'Manrope' }}>My Dashboard</h1>
            <p className="text-xs text-slate-600">{user?.full_name}</p>
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" size="icon" onClick={() => toast.info('Notifications feature coming soon!')} data-testid="notifications-button">
              <Bell className="h-5 w-5 text-slate-600" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => navigate('/settings')} data-testid="settings-button">
              <Settings className="h-5 w-5 text-slate-600" />
            </Button>
            <Button variant="ghost" size="icon" onClick={logout} data-testid="logout-button">
              <LogOut className="h-5 w-5 text-slate-600" />
            </Button>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* CHANGE 9: Stats Cards - Now Clickable with navigation to filtered leads */}
        <div className="grid grid-cols-2 gap-3">
          {/* Backlog - Clickable: navigates to leads with overdue next_call_date */}
          <Card 
            className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/leads?filter=backlog')}
          >
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-orange-700">{stats?.backlog || 0}</div>
              <div className="text-xs text-orange-600 font-medium">Backlog (Overdue)</div>
            </CardContent>
          </Card>
          
          {/* Site Visits Done - Clickable: navigates to leads with status=Site Visit Done */}
          <Card 
            className="bg-gradient-to-br from-green-50 to-green-100 border-green-200 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/leads?filter=site_visits_done')}
          >
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-green-700">{stats?.site_visits_done || 0}</div>
              <div className="text-xs text-green-600 font-medium">Site Visits Done</div>
            </CardContent>
          </Card>
          
          {/* Today's Tasks - Clickable: navigates to leads with next_call_date=today */}
          <Card 
            className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/leads?filter=today')}
          >
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-amber-700">{stats?.today_followups || 0}</div>
              <div className="text-xs text-amber-600 font-medium">Today's Tasks</div>
            </CardContent>
          </Card>
          
          {/* Pending Visits - Clickable: navigates to leads with status=Site Visit Planned */}
          <Card 
            className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/leads?filter=pending_visits')}
          >
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-purple-700">{stats?.pending_visits || 0}</div>
              <div className="text-xs text-purple-600 font-medium">Pending Visits</div>
            </CardContent>
          </Card>
        </div>

        {/* CHANGE 7 & MODIFICATION: Quick Actions - Added Calendar button */}
        <div className="grid grid-cols-4 gap-3">
          <Button
            className="h-14 bg-cyan-600 hover:bg-cyan-700 text-white font-medium"
            onClick={() => navigate('/leads/new')}
            data-testid="add-lead-button"
          >
            <Plus className="mr-2 h-5 w-5" />
            Add Lead
          </Button>
          <Button
            className="h-14 bg-slate-700 hover:bg-slate-800 text-white font-medium"
            onClick={() => navigate('/leads')}
            data-testid="total-leads-button"
          >
            <LayoutDashboard className="mr-2 h-5 w-5" />
            Total Leads ({stats?.total_leads || 0})
          </Button>
          <Button
            className="h-14 bg-purple-600 hover:bg-purple-700 text-white font-medium"
            onClick={() => navigate('/reports')}
            data-testid="reports-button"
          >
            <BarChart3 className="mr-2 h-5 w-5" />
            Reports
          </Button>
          {/* CHANGE 7: Calendar Button for Sales Rep */}
          <Button
            className="h-14 bg-indigo-600 hover:bg-indigo-700 text-white font-medium"
            onClick={() => navigate('/calendar')}
            data-testid="calendar-button"
          >
            <CalendarDays className="mr-2 h-5 w-5" />
            Calendar
          </Button>
        </div>

        {/* Today's Tasks */}
        {todayLeads.length > 0 && (
          <Card data-testid="today-tasks-section">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold flex items-center">
                <Calendar className="mr-2 h-5 w-5 text-amber-600" />
                Today's Tasks
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {todayLeads.map(lead => (
                <div
                  key={lead.id}
                  className="p-3 bg-amber-50 border border-amber-200 rounded-lg cursor-pointer hover:bg-amber-100 transition-colors"
                  onClick={() => navigate(`/leads/${lead.id}`)}
                  data-testid={`followup-lead-${lead.id}`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="font-semibold text-slate-800">{lead.customer_name}</div>
                      <div className="text-xs text-slate-600 flex items-center mt-1">
                        <Phone className="h-3 w-3 mr-1" />
                        <a href={`tel:${lead.mobile}`} className="hover:text-cyan-600" onClick={(e) => e.stopPropagation()}>{lead.mobile}</a>
                      </div>
                    </div>
                    <Badge className={getStatusColor(lead.lead_status)}>{lead.lead_status}</Badge>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Recent Leads */}
        <Card data-testid="recent-leads-section">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold">Recent Leads</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {recentLeads.map(lead => (
              <div
                key={lead.id}
                className="p-3 bg-white border border-slate-200 rounded-lg cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => navigate(`/leads/${lead.id}`)}
                data-testid={`recent-lead-${lead.id}`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="font-semibold text-slate-800">{lead.customer_name}</div>
                    <div className="text-xs text-slate-600 flex items-center mt-1">
                      <Phone className="h-3 w-3 mr-1" />
                      <a href={`tel:${lead.mobile}`} className="hover:text-cyan-600" onClick={(e) => e.stopPropagation()}>{lead.mobile}</a>
                    </div>
                    {lead.projects?.length > 0 && (
                      <div className="text-xs text-slate-500 flex items-center mt-1">
                        <MapPin className="h-3 w-3 mr-1" />
                        {lead.projects[0]}{lead.projects.length > 1 && ` +${lead.projects.length - 1}`}
                      </div>
                    )}
                  </div>
                  <Badge className={getStatusColor(lead.lead_status)}>{lead.lead_status}</Badge>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default BrokerDashboard;
